/**
 * The sentence both places say, byte for byte.
 *
 * **Do not paraphrase it at a call site, and do not write a second spelling of the claim
 * beside it.** The whole failure this constant exists to prevent is two true sentences that
 * differ, one of which then reads as a threat.
 *
 * Deliberately phrased in "our address" / "yours" rather than naming a host: it has to read
 * correctly in the domain-setup explainer, where no platform host is on screen, and in the
 * artwork hatch, where one is. A sentence that had to interpolate a hostname could not be
 * one sentence.
 */
export const PERMANENCE_SENTENCE = "A code printed with our address can never be moved to yours without reprinting the labels.";
/**
 * §8.3's disclosure, for one organisation's platform host.
 *
 * §8.3 requires the substance rather than the wording: *"These codes will answer forever —
 * but they can never be moved to your own domain without reprinting. If you want codes you
 * own, wait for `id.yourbrand.com` to go live."* Three claims, and they are three fields
 * because a screen has to be able to weight the middle one — *"disclosed at the moment, not
 * buried"* is a layout instruction as much as a copy one.
 *
 * The first claim is not softening. It is the true part, and a disclosure that led with the
 * cost would be selling the customer's own domain rather than informing a choice: platform
 * codes really do answer forever, including after cancellation (invariant 1), and a user
 * who does not believe that will not believe the second sentence either.
 *
 * ## Two contexts, one disclosure, and the difference is only the last sentence
 *
 * §8.3 was written about one situation — a user blocked on gate 1, escaping. There is a
 * second, and it is the commoner one: an organisation with **no custom domain configured
 * at all** is never gated, and its artwork encodes a platform URI on the ordinary path. The
 * permanence cost is identical, so the disclosure fires in both cases. A regime that
 * disclosed only on the escape would be silent on the majority of platform prints, which is
 * §8.3's own *"silently, this converts a user… over impatience"* reproduced at scale.
 *
 * What differs is what to wait for. A blocked user has a domain in flight and can be told to
 * wait for it by name; a user with none has a step to take first. Same first two sentences,
 * byte for byte.
 *
 * @param customDomain The host the user was blocked on, e.g. `id.riverbend.com`, when there
 *   is one. Omitted when the organisation has no custom domain configured — the alternative
 *   then names the step rather than a hostname that does not exist yet.
 */
export function escapeHatchDisclosure(customDomain) {
    return {
        reassurance: "Printing against a packagepage address is safe. It resolves today, and we keep " +
            "answering it for as long as those packs exist — including if you cancel.",
        permanence: PERMANENCE_SENTENCE,
        alternative: customDomain === undefined
            ? "If you want codes you own, set up a custom domain first and print against " +
                "that. It is your domain, so those codes keep working wherever you take them."
            : `If you want codes you own, wait until ${customDomain} answers and print then. ` +
                `It is your domain, so those codes keep working wherever you take them.`,
    };
}
/**
 * The domain-setup pre-setup explainer's ownership paragraph — `screens.md` §8, section A.
 *
 * **Copy landing ahead of the screen that renders it, deliberately.** Phase 4 builds domain
 * setup; this is here now because it is the *other* side of {@link PERMANENCE_SENTENCE}, and
 * a shared constant with one consumer is a shared constant that proves nothing. Both halves
 * of the constraint exist from this commit, so the day Phase 4 writes the screen it composes
 * this rather than authoring a second sentence — which is the failure the handoff's own
 * rationale note predicts and the handoff itself then commits.
 *
 * Only the ownership paragraph. `screens.md` §8's other non-negotiables — the
 * `edge.pkgedge.com` CNAME target and the *"this is a new subdomain; it cannot affect your
 * existing website or email"* preempt — are **not** here, because they describe machinery
 * Phase 4 builds and this module does not write copy for things that do not exist.
 */
export const DOMAIN_SETUP_EXPLAINER = {
    headline: "Print your own address, own your codes",
    ownership: "A code printed with your own domain is yours permanently. It is your domain, and " +
        "it keeps working even if you leave us. " +
        PERMANENCE_SENTENCE,
};
//# sourceMappingURL=artwork-copy.js.map